import { ExpandLess, ExpandMore } from "@mui/icons-material";
import BugReportRoundedIcon from "@mui/icons-material/BugReportRounded";
import { Box, Collapse, Divider, IconButton, Typography } from "@mui/material";
import pluralize from "pluralize";
import { useContext, useState } from "react";
import useAPI from "../../../../../api/hooks/useAPI";
import useColors from "../../../../../api/hooks/useColors";
import ToggleButton from "../../../../../components/buttons/ToggleButton";
import ToggleGroup from "../../../../../components/buttons/ToggleGroup";
import CustomToolTip from "../../../../../components/CustomTooltip";
import { EnvFeatureFlagContext } from "../../../../../contexts/EnvFeaturesContext";
import { ReactComponent as Warning } from "../../../../../icons/icon_files/severity_high.svg";
import SvgToIcon from "../../../../../icons/SvgToIcon";
import OverviewGauge from "../../components/EntityTypeGauge/OverviewGauge";
import { overviewType } from "../../EnvironmentView";
import { getInitialFilters } from "../../filters/utils";
import { ReactComponent as CardViewSVG } from "../../icons/card_view_24.svg";
import { ReactComponent as TableViewSVG } from "../../icons/table_view_24.svg";
import OverviewFilters from "../overviewFilters/OverviewFilters";
import InventoryViewControls from "./components/InventoryViewControls";
export const MIN_TOP_OVERVIEW_WIDTH = 1220;

export default function EntityTopOverview({
  overviewData,
  useQueryForAllEntities,
  assets,
  width,
  selectedOverviewType,
  setSelectedOverviewType,
  appliedFilters,
  setAppliedFilters,
  filterAndOrLogics,
  setFilterAndOrLogics,
  setPostgresFilteredEntities,
  setIsSearching,
  visibleColumns,
  setVisibleColumns,
  onRdsSearchApply,
  onRdsSearchClear,
}) {
  const colors = useColors();
  const { trackEvent } = useAPI();
  const { isActiveRds } = useContext(EnvFeatureFlagContext);
  const [isExpanded, setIsExpanded] = useState(true);
  const [entityFilters, setEntityFilters] = useState([]);
  const [entityFilterLogic, setEntityFilterLogic] = useState([]);

  const activeFiltersCount = Object.values(appliedFilters || {}).filter(
    (val) => {
      if (val == null) return false;
      if (Array.isArray(val) || typeof val === "string") return val.length > 0;
      if (typeof val === "object") return Object.keys(val).length > 0;
      return !!val;
    }
  ).length;

  const entitiesInfo = [
    { quantity: overviewData.total_entities, title: "Total Entity" },
    {
      quantity: overviewData.new_entities,
      title: "New Entity",
      tooltipTitle: "New within the last 7 days",
    },
    {
      quantity: overviewData.high_privilege_identities,
      title: "High-Privilege Identity",
    },
  ];

  const riskInfo = [
    {
      quantity: overviewData.high_severity_gaps,
      title: "Vulnerable to high severity gaps",
      icon: Warning,
      iconWidth: "22px",
    },
    {
      quantity: overviewData.entities_vulnerable_to_critical_cves,
      title: "Vulnerable to critical CVEs",
      icon: BugReportRoundedIcon,
      iconWidth: "30px",
    },
  ];

  const adjustTitle = (item) => {
    if (item.quantity === 1) return item.title;
    if (item?.pluralize) return item.pluralize;
    return pluralize(item.title, item.quantity);
  };

  const handleToggleCardView = () => {
    trackEvent("User clicked on the environment overview card view button");
    const newEmptyFilter = getInitialFilters();
    setAppliedFilters(newEmptyFilter);
    setSelectedOverviewType(overviewType.CARD);
  };

  const handleToggleTableView = () => {
    trackEvent("User clicked on the environment overview table view button");
    setSelectedOverviewType(overviewType.TABLE);
  };

  return (
    <Box
      minWidth={`${MIN_TOP_OVERVIEW_WIDTH}px`}
      padding="16px"
      display="flex"
      flexDirection="column"
      flexWrap="wrap"
      gap={2}
      sx={{
        borderRadius: "12px",
        backgroundColor: colors.grey_2percent,
        boxShadow: colors.entity_type_overview_top_shadow,
      }}
    >
      <Box height="75px" display="flex" gap={4}>
        <Box minWidth="131px" paddingRight={2} position="relative">
          <Box display="flex" justifyContent="flex-end">
            <Typography variant="subtitle1">Coverage</Typography>
          </Box>
          <Box position="relative" left="38px" bottom="5px">
            <OverviewGauge value={overviewData.coverage} />
          </Box>
        </Box>
        <Divider orientation="vertical" sx={{ borderColor: colors.divider }} />
        <Box display="flex" gap={4} alignItems="center">
          {entitiesInfo.map((item) => (
            <Box key={item.title} position="relative">
              <Box display="flex" flexDirection="column" gap={0.5} paddingY={1}>
                <Typography variant="h6">{item.quantity}</Typography>
                <Typography variant="body2" color={colors.text_primary} noWrap>
                  {adjustTitle(item)}
                </Typography>
              </Box>
              <CustomToolTip
                key={item.title}
                title={item.tooltipTitle}
                placement="top"
              >
                <Box
                  position="absolute"
                  width="90%"
                  height="55%"
                  top="18px"
                  left="0"
                ></Box>
              </CustomToolTip>
            </Box>
          ))}
        </Box>
        <Divider orientation="vertical" sx={{ borderColor: colors.divider }} />
        <Box display="flex" gap={4} alignItems="center">
          {riskInfo.map((item) => (
            <Box key={item.title} display="flex" gap={2}>
              <Box display="flex" alignItems="center">
                <SvgToIcon
                  IconLogo={item.icon}
                  width={item.iconWidth}
                  style={{ color: colors.error_main }}
                />
              </Box>
              <Box display="flex" flexDirection="column" gap={0.5} paddingY={1}>
                <Typography variant="h6">{item.quantity}</Typography>
                <Typography variant="body2" color={colors.text_primary} noWrap>
                  {adjustTitle(item)}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
      <Divider sx={{ borderColor: colors.divider }} />

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        paddingY={1}
      >
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          onClick={() => setIsExpanded(!isExpanded)}
          sx={{ cursor: "pointer", userSelect: "none" }}
        >
          <Typography variant="h6" fontWeight={600} color={colors.text_primary}>
            Filters
          </Typography>
          {!isExpanded && activeFiltersCount > 0 && (
            <Typography variant="body2" color={colors.text_secondary}>
              ({activeFiltersCount} active)
            </Typography>
          )}
        </Box>

        <Box display="flex" alignItems="center" gap={2}>
          {isActiveRds && (
            <InventoryViewControls
              appliedFilters={appliedFilters}
              setAppliedFilters={setAppliedFilters}
              entityFilters={entityFilters}
              setEntityFilters={setEntityFilters}
              entityFilterLogic={entityFilterLogic}
              setEntityFilterLogic={setEntityFilterLogic}
              setPostgresFilteredEntities={setPostgresFilteredEntities}
              setIsSearching={setIsSearching}
              handleToggleTableView={handleToggleTableView}
              visibleColumns={visibleColumns}
              setVisibleColumns={setVisibleColumns}
            />
          )}
          <ToggleGroup>
            <ToggleButton
              iconSVG={CardViewSVG}
              selected={selectedOverviewType === overviewType.CARD}
              onClick={handleToggleCardView}
              disabled={false}
            >
              <Typography variant="overline">Cards View</Typography>
            </ToggleButton>
            <ToggleButton
              iconSVG={TableViewSVG}
              selected={selectedOverviewType === overviewType.TABLE}
              onClick={handleToggleTableView}
              disabled={false}
            >
              <Typography variant="overline">Table View</Typography>
            </ToggleButton>
          </ToggleGroup>
          <IconButton
            onClick={() => setIsExpanded(!isExpanded)}
            size="small"
            sx={{ color: colors.text_secondary }}
          >
            {isExpanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>
      </Box>

      <Collapse in={isExpanded} timeout="auto">
        <Box paddingBottom={1}>
          {/*  TODO - consider add a loading here */}
          <OverviewFilters
            fetchedEntities={useQueryForAllEntities.data || []}
            assets={assets}
            appliedFilters={appliedFilters}
            setAppliedFilters={setAppliedFilters}
            filterAndOrLogics={filterAndOrLogics}
            setFilterAndOrLogics={setFilterAndOrLogics}
            setPostgresFilteredEntities={setPostgresFilteredEntities}
            setIsSearching={setIsSearching}
            handleToggleTableView={handleToggleTableView}
            entityFilters={entityFilters}
            setEntityFilters={setEntityFilters}
            entityFilterLogic={entityFilterLogic}
            setEntityFilterLogic={setEntityFilterLogic}
            onRdsSearchApply={onRdsSearchApply}
            onRdsSearchClear={onRdsSearchClear}
          />
        </Box>
      </Collapse>
    </Box>
  );
}
